// Port of Flutter's forgotPassScreen.dart — a single card whose button label
// walks through three states: Send OTP → Verify OTP → Reset Password. Each step
// reveals the next field and locks the mobile number once the OTP is out.
//
// The reset itself writes BOTH the new Password and this install's DeviceName
// (forgotPassFunctions.dart updateId), which is what logs the account out of
// other phones, then signs in here.

import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import PasswordIcon from '@mui/icons-material/Password';
import { useNavigate } from 'react-router-dom';
import GlassCard from '../components/GlassCard';
import AuthField from '../components/AuthField';
import AuthButton from '../components/AuthButton';
import { useMessage } from '../components/MessageDialog';
import { useApp } from '../../../core/state/AppContext';
import { resetPasswordAndBindDevice } from '../../../core/api/api';
import { requestOtp, attemptLogin } from '../../../core/auth/loginFlow';
import { getDeviceId } from '../../../core/utils/deviceId';

const HEADING_FONT = "'BetterFonts', 'Satisfy', cursive";

export default function MobileForgotPasswordScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { setSession } = useApp();
  const { show } = useMessage();

  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [pass, setPass] = useState('');
  const [sentOtp, setSentOtp] = useState(null); // the code from the server
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [busy, setBusy] = useState(false);

  const label = otpSent && !otpVerified ? 'Verify OTP'
    : otpVerified ? 'Reset Password' : 'Send OTP';

  const submit = async () => {
    if (mobile.length !== 10) {
      show('Invalid Number', 'Number must be 10 digits long.');
      return;
    }

    if (!otpSent) {
      setBusy(true);
      const code = await requestOtp(mobile);
      setBusy(false);
      setSentOtp(code);
      setOtpSent(true);
      return;
    }

    if (!otpVerified) {
      if (otp === String(sentOtp ?? '')) { setOtpVerified(true); return; }
      show('Invalid OTP', 'Please check the OTP entered');
      return;
    }

    if (!pass) {
      show('Invalid Password', 'The password cannot be empty');
      return;
    }

    setBusy(true);
    try {
      const deviceName = await getDeviceId();
      await resetPasswordAndBindDevice(mobile, pass, deviceName);
      const result = await attemptLogin(mobile, pass);
      if (result.status === 'ok') {
        setSession(result.userData);
        navigate('/home', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (e) {
      show('Reset Failed', 'Could not reset your password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 32, color: c.text, fontWeight: 700, fontFamily: HEADING_FONT, textAlign: 'center' }}>
          Reset your password
        </Typography>

        <Box sx={{ height: 20 }} />
        <AuthField
          placeholder="Mobile Number"
          icon={<PhoneIcon />}
          numeric
          maxLength={10}
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
          InputProps={undefined}
          disabled={otpSent}
        />

        <Box sx={{ height: 20 }} />
        {otpSent && (
          <>
            <AuthField
              placeholder="OTP"
              icon={<EnhancedEncryptionIcon />}
              numeric
              maxLength={4}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
            />
            <Box sx={{ height: 20 }} />
          </>
        )}

        {otpVerified && (
          <>
            <AuthField
              placeholder="New Password"
              icon={<PasswordIcon />}
              numeric
              maxLength={4}
              value={pass}
              onChange={(e) => setPass(e.target.value.replace(/\D/g, '').slice(0, 4))}
            />
            <Box sx={{ height: 20 }} />
          </>
        )}

        <AuthButton onClick={submit} disabled={busy}>{label}</AuthButton>
      </Box>
    </GlassCard>
  );
}
